import { ApolloLink, FetchResult, Observable, Operation } from '@apollo/client';
import { print } from 'graphql';

export interface SSELinkOptions {
	uri: string;
	headers?: () => Record<string, string>;
}

/** Backoff between reconnects: 1s doubling to a 30s ceiling. */
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

/**
 * Subscriptions over Server-Sent Events.
 *
 * Reconnects on its own. A stream ends for ordinary reasons — the server
 * restarts, a proxy times the connection out, a laptop sleeps — and completing
 * or erroring the observer would end the subscription permanently, since Apollo
 * does not resubscribe on either. That was survivable when notifications only
 * followed a user's own actions, because navigating refetched anyway. It stopped
 * being survivable once scheduled jobs began raising notifications at 09:00 on a
 * tab left open overnight, with nothing to piggyback on.
 *
 * Note what a reconnect does *not* do: anything raised while disconnected is
 * never delivered, because there is no replay. Consumers that must be correct —
 * an unread badge, say — still need to poll or refetch. This only guarantees the
 * stream comes back, not that nothing was missed.
 *
 * The one end that is NOT reconnected: the server finishing the subscription
 * itself, which it announces with an `event: complete` line (GraphQL Yoga's SSE
 * protocol). That is deliberate — a product thread's stream ends when the viewer
 * loses access — so it completes the observer instead. A restart or a proxy
 * timeout drops the connection without that line, and still reconnects.
 */
export class SSELink extends ApolloLink {
	private uri: string;
	private headers: () => Record<string, string>;

	constructor(options: SSELinkOptions) {
		super();
		this.uri = options.uri;
		this.headers = options.headers || (() => ({}));
	}

	public request(operation: Operation): Observable<FetchResult> | null {
		return new Observable<FetchResult>((observer) => {
			let unsubscribed = false;
			let attempt = 0;
			let retryTimer: ReturnType<typeof setTimeout> | undefined;
			let controller: AbortController | undefined;

			const scheduleReconnect = () => {
				if (unsubscribed) return;
				const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
				attempt += 1;
				retryTimer = setTimeout(connect, delay);
			};

			const connect = async () => {
				if (unsubscribed) return;
				controller = new AbortController();

				try {
					// GraphQL Yoga expects a POST request with SSE response
					const response = await fetch(this.uri, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							Accept: 'text/event-stream',
							...this.headers(),
						},
						// Send HttpOnly cookies so authenticated subscriptions work
						// cross-origin.
						credentials: 'include',
						body: JSON.stringify({
							query: print(operation.query),
							variables: operation.variables,
							operationName: operation.operationName,
						}),
						signal: controller.signal,
					});

					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}

					const reader = response.body?.getReader();
					if (!reader) {
						throw new Error('No response body');
					}

					// Only reset the backoff once a stream is genuinely open,
					// otherwise a server that accepts and immediately drops every
					// connection would be retried a thousand times a second.
					attempt = 0;

					const decoder = new TextDecoder();
					let buffer = '';
					let serverCompleted = false;

					for (;;) {
						const { done, value } = await reader.read();
						if (done) break;

						buffer += decoder.decode(value, { stream: true });
						const lines = buffer.split('\n');

						// Keep the last incomplete line in the buffer
						buffer = lines.pop() || '';

						for (const line of lines) {
							if (line.trim() === 'event: complete') {
								serverCompleted = true;
								continue;
							}
							if (line.startsWith('data: ')) {
								try {
									const data = JSON.parse(line.slice(6));
									observer.next(data);
								} catch (error) {
									console.error('Failed to parse SSE data:', error);
								}
							}
						}
					}
					// The server ended the subscription on purpose — final, not a drop.
					if (serverCompleted) {
						if (!unsubscribed) observer.complete();
						return;
					}
				} catch (error) {
					// An abort is this link being torn down, not a failure.
					if (unsubscribed || (error as Error)?.name === 'AbortError') return;
					console.warn('SSE subscription dropped, reconnecting:', error);
				}

				// Reached on a clean end of stream and on a failed connection alike:
				// both mean there is no live stream, and both should come back.
				scheduleReconnect();
			};

			connect();

			return () => {
				unsubscribed = true;
				clearTimeout(retryTimer);
				controller?.abort();
			};
		});
	}
}
